import { useMemo } from "react";

// Frutiger Aero scene — sky gradient, drifting clouds, bubbles, optional fish
export type Variant = "aqua" | "mercury" | "reef";

export default function AeroScene({ variant = "mercury" }: { variant?: Variant }) {
  const isMercury = variant === "mercury";
  const isReef = variant === "reef";

  const bg = isMercury
    ? `radial-gradient(70% 60% at 30% 20%, rgba(220,240,255,0.55), transparent 70%),
       radial-gradient(60% 50% at 80% 30%, rgba(180,220,255,0.45), transparent 72%),
       radial-gradient(80% 65% at 50% 90%, rgba(120,160,200,0.35), transparent 75%),
       linear-gradient(180deg, #6ba8d8 0%, #4a8bc4 35%, #2d5a8a 100%)`
    : isReef
    ? `radial-gradient(60% 50% at 18% 22%, rgba(190,255,235,0.45), transparent 70%),
       radial-gradient(55% 45% at 82% 28%, rgba(150,235,255,0.4), transparent 72%),
       radial-gradient(70% 55% at 28% 78%, rgba(180,255,220,0.32), transparent 74%),
       radial-gradient(65% 50% at 74% 72%, rgba(110,210,240,0.3), transparent 72%),
       linear-gradient(180deg, #2db5b8 0%, #1e8a96 50%, #0f5a6a 100%)`
    : `radial-gradient(60% 50% at 18% 18%, rgba(200,250,240,0.4), transparent 70%),
       radial-gradient(55% 45% at 82% 22%, rgba(160,225,255,0.42), transparent 72%),
       radial-gradient(70% 55% at 28% 82%, rgba(190,255,235,0.32), transparent 74%),
       radial-gradient(65% 50% at 74% 72%, rgba(140,220,245,0.32), transparent 72%),
       linear-gradient(180deg, #4ec1cc 0%, #2d99a8 48%, #155f6e 100%)`;

  const clouds = useMemo(() => {
    const seed = isMercury ? 8 : isReef ? 6 : 7;
    return Array.from({ length: seed }).map((_, i) => ({
      id: i,
      top: 5 + ((i * 137) % 80),
      left: -10 + ((i * 211) % 110),
      scale: 0.6 + ((i * 13) % 60) / 100,
      dur: 80 + ((i * 17) % 60),
      delay: -((i * 11) % 40),
      opacity: isMercury ? 0.45 : 0.32,
    }));
  }, [variant]);

  const bubbles = useMemo(() => {
    const n = isReef ? 28 : 18;
    return Array.from({ length: n }).map((_, i) => ({
      id: i,
      left: (i * 53) % 100,
      size: 8 + ((i * 19) % 40),
      dur: 12 + ((i * 7) % 16),
      delay: -((i * 5) % 18),
      opacity: 0.18 + ((i * 7) % 30) / 100,
    }));
  }, [variant]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden", background: bg }}>
      <div style={{
        position: "absolute", left: 0, right: 0, top: "20%", height: "30%",
        background: isMercury
          ? "linear-gradient(180deg, rgba(255,255,255,0.18), transparent)"
          : "linear-gradient(180deg, rgba(220,255,245,0.12), transparent)",
        filter: "blur(20px)",
      }} />

      {clouds.map(c => (
        <div key={c.id} style={{
          position: "absolute", top: `${c.top}%`, left: `${c.left}%`,
          transform: `scale(${c.scale})`, opacity: c.opacity,
          animation: `drift ${c.dur}s linear ${c.delay}s infinite`,
          willChange: "transform",
        }}>
          <svg width="220" height="80" viewBox="0 0 220 80" style={{ filter: "blur(0.4px) drop-shadow(0 8px 18px rgba(255,255,255,0.18))" }}>
            <defs>
              <radialGradient id={`cg${c.id}`} cx="50%" cy="40%" r="60%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
                <stop offset="60%" stopColor="rgba(255,255,255,0.6)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </radialGradient>
            </defs>
            <ellipse cx="60" cy="50" rx="50" ry="22" fill={`url(#cg${c.id})`} />
            <ellipse cx="100" cy="40" rx="55" ry="26" fill={`url(#cg${c.id})`} />
            <ellipse cx="150" cy="48" rx="45" ry="22" fill={`url(#cg${c.id})`} />
            <ellipse cx="180" cy="55" rx="32" ry="16" fill={`url(#cg${c.id})`} />
          </svg>
        </div>
      ))}

      {bubbles.map(b => (
        <div key={`b${b.id}`} style={{
          position: "absolute", bottom: -60, left: `${b.left}%`,
          width: b.size, height: b.size, borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.7), rgba(255,255,255,0.1) 50%, rgba(180,240,255,0.05) 100%)",
          border: "1px solid rgba(255,255,255,0.35)",
          boxShadow: "inset 2px 2px 4px rgba(255,255,255,0.4), inset -1px -1px 2px rgba(120,200,230,0.2), 0 0 8px rgba(180,240,255,0.3)",
          opacity: b.opacity,
          animation: `bubbleRise ${b.dur}s ease-in ${b.delay}s infinite`,
          willChange: "transform, opacity",
        } as React.CSSProperties} />
      ))}

      {isReef && Array.from({ length: 4 }).map((_, i) => (
        <div key={`f${i}`} style={{
          position: "absolute",
          top: `${15 + i * 22}%`,
          left: i % 2 === 0 ? "-8%" : "108%",
          animation: `swim${i % 2} ${30 + i * 7}s linear ${-i * 5}s infinite`,
          opacity: 0.5,
        }}>
          <svg width="60" height="36" viewBox="0 0 60 36" style={{ transform: i % 2 === 0 ? "none" : "scaleX(-1)" }}>
            <defs>
              <linearGradient id={`fg${i}`} x1="0" x2="1">
                <stop offset="0%" stopColor="#fff8c8" />
                <stop offset="50%" stopColor="#ffd84a" />
                <stop offset="100%" stopColor="#1a3a4a" />
              </linearGradient>
            </defs>
            <ellipse cx="22" cy="18" rx="20" ry="9" fill={`url(#fg${i})`} stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" />
            <polygon points="42,18 58,8 58,28" fill="#1a3a4a" opacity="0.8" />
            <circle cx="14" cy="16" r="1.6" fill="#1a3a4a" />
            <ellipse cx="22" cy="14" rx="6" ry="2" fill="rgba(255,255,255,0.4)" />
          </svg>
        </div>
      ))}

      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.05) 45%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 55%, transparent 70%)",
        animation: "caustic 18s ease-in-out infinite",
        mixBlendMode: "screen",
      }} />

      <div style={{
        position: "absolute", top: -60, right: -60, width: 320, height: 320,
        background: "conic-gradient(from 200deg, transparent 0deg, rgba(255,180,200,0.1) 30deg, rgba(255,230,150,0.12) 60deg, rgba(180,255,200,0.1) 90deg, rgba(150,220,255,0.12) 120deg, rgba(200,180,255,0.1) 150deg, transparent 180deg)",
        filter: "blur(40px)", opacity: 0.7, pointerEvents: "none",
      }} />
    </div>
  );
}
